/* eslint-disable jsx-a11y/anchor-is-valid */
import ct from "class-types.macro";
import React, { useRef, useState } from "react";
import { SubmitHandler, UseFormReturn } from "react-hook-form";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { z } from "zod";
import Form from "~/common/Form";
import Input from "~/common/Form/Input";
const schema = z.object({
  cost: z.coerce.number({
    message: "Unit Cost must be a number"
  }).positive("").gt(0),
  price: z.coerce.number({
    message: "Price must be a number"
  }).positive("").gt(0),
  servings: z.coerce.number({
    message: "Servings must be a number"
  }).positive("").gt(0)
});
interface Props {
  defaultTitle?: string;
  defaultCost?: string;
  defaultPrice?: string;
  defaultServings?: string;
}
type FormFields = z.infer<typeof schema>;
function ProfitCalculator({
  defaultTitle = "Generated Profits Calculator",
  defaultCost = "0.26",
  defaultPrice = "1",
  defaultServings = "20"
}: Props) {
  const [dayProfits, setDayProfits] = useState("");
  const [monthProfits, setMonthProfits] = useState("");
  const [yearProfits, setYearProfits] = useState("");
  const formRef = useRef<UseFormReturn<FormFields>>();
  const resetForm = (e: React.SyntheticEvent) => {
    e.preventDefault();
    resetProfits();
    formRef.current?.reset();
  };
  const resetProfits = () => {
    setDayProfits("");
    setMonthProfits("");
    setYearProfits("");
  };
  const handleSubmit: SubmitHandler<FormFields> = data => {
    const costValue = data.cost;
    const priceValue = data.price;
    const servingsValue = data.servings;
    const profitsPerUnit = (priceValue - costValue) * servingsValue;
    if (!Number.isNaN(profitsPerUnit)) {
      setDayProfits(formatAsCurrency.formatIfUndefined(profitsPerUnit));
      setMonthProfits(formatAsCurrency.formatIfUndefined(profitsPerUnit * 30));
      setYearProfits(formatAsCurrency.formatIfUndefined(profitsPerUnit * 30 * 12));
    }
  };
  const outputStyles: React.CSSProperties = {
    borderRadius: "4px",
    boxShadow: "inset 0 1px 4px rgb(0 0 0 / 4%), 0 1px 0 #fff",
    border: "1px solid rgba(0,0,0,.12)",
    background: "rgba(0,0,0,.04)"
  };
  return <div style={{
    borderRadius: "4px",
    boxShadow: "0 1px 4px rgb(0 0 0 / 8%), 0 1px 0 white inset"
  }} className={ct("bg-gray-200", "border-gray-200", "border-solid", "border", "leading-6", "mx-auto", "mt-5", "mb-10", "text-left", "w-4/5", "xxl:w-11/12")} data-sentry-component="ProfitCalculator" data-sentry-source-file="ProfitCalculator.tsx">
      <div className={ct("border-gray-200", "border-solid", "border", "border-t-0", "border-r-0", "border-l-0", "border-b", "py-3", "px-5")}>
        <h4 style={{
        textShadow: "0 1px 0 #fff"
      }} className={ct("text-xl", "leading-6", "m-0")}>
          {defaultTitle}
        </h4>
      </div>
      <Form<FormFields> validationSchema={schema} onSubmit={handleSubmit} formMethodsRef={formRef} data-sentry-element="Form" data-sentry-source-file="ProfitCalculator.tsx">
        <div style={{
        textShadow: "0 1px 0 white"
      }} className={ct("text-xl", "px-5", "py-4-1/2")}>
          <div data-testid="input-container">
            <Input placeholder="Enter Cost" label="Unit Cost" name="cost" labelClassName={ct("font-semibold", "mb-2", "text-gray-800")} defaultValues={defaultCost} data-testid="cost" data-sentry-element="Input" data-sentry-source-file="ProfitCalculator.tsx" />

            <Input placeholder="Enter Price" label="Retail Price" name="price" labelClassName={ct("font-semibold", "mb-2", "text-gray-800")} defaultValues={defaultPrice} data-testid="price" data-sentry-element="Input" data-sentry-source-file="ProfitCalculator.tsx" />

            <Input placeholder="Enter Price" label="Servings Per Day" name="servings" labelClassName={ct("font-semibold", "mb-2", "text-gray-800")} defaultValues={defaultServings} data-testid="servings" data-sentry-element="Input" data-sentry-source-file="ProfitCalculator.tsx" />
          </div>

          <hr className={ct("mt-4", "mb-8")} />

          <div style={outputStyles} className={ct("py-3", "px-4")}>
            <div style={{
            borderBottom: "1px solid rgba(0,0,0,.08)"
          }} className={ct("font-semibold", "text-base", "mb-1", "pb-1", "text-gray-800")}>
              Profit Per Day
            </div>
            <div data-testid="dayProfit" className={ct("font-semibold", "text-2xl", "text-gray-900")}>
              {dayProfits}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div style={{
            borderBottom: "1px solid rgba(0,0,0,.08)"
          }} className={ct("font-semibold", "text-base", "mb-1", "pb-1", "text-gray-800")}>
              Profit Per Month
            </div>
            <div data-testid="monthProfit" className={ct("font-semibold", "text-2xl", "text-gray-900")}>
              {monthProfits}
            </div>
          </div>

          <div style={outputStyles} className={ct("mt-8", "py-3", "px-4")}>
            <div style={{
            borderBottom: "1px solid rgba(0,0,0,.08)"
          }} className={ct("font-semibold", "text-base", "mb-1", "pb-1", "text-gray-800")}>
              Profit Per Year
            </div>
            <div data-testid="yearProfit" className={ct("font-semibold", "text-2xl", "text-gray-900")}>
              {yearProfits}
            </div>
          </div>
        </div>
        <div className={ct("border-gray-200", "border-solid", "border", "border-r-0", "border-b-0", "border-l-0", "border-t", "px-5", "py-2-1/2")}>
          <button type="submit" className="btn btn-primary" data-testid="hold-oven-calculate-button">
            Calculate
          </button>
          <a href="" className={ct("float-right", "text-base", "py-2")} style={{
          textShadow: "0 1px 0 #fff"
        }} onClick={resetForm} data-testid="hold-oven-reset-button">
            Reset
          </a>
        </div>
      </Form>
    </div>;
}
export default ProfitCalculator;